import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Copy,
  Loader2,
  RefreshCw,
  ExternalLink,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface VerificationResult {
  success: boolean;
  aRecordValid: boolean;
  txtRecordValid: boolean;
  errors: string[];
  details: {
    expectedARecord: string;
    expectedTxtRecord: string;
    foundARecords: string[];
    foundTxtRecords: string[];
  };
}

interface DNSVerificationStatusProps {
  domain: string;
  domainVerified: boolean;
  domainVerificationToken: string;
  lastVerificationCheck?: string;
  verificationErrors?: string[];
  onVerify: () => Promise<VerificationResult>;
  onRemoveDomain: () => void;
}

export function DNSVerificationStatus({
  domain,
  domainVerified,
  domainVerificationToken,
  lastVerificationCheck,
  verificationErrors = [],
  onVerify,
  onRemoveDomain,
}: DNSVerificationStatusProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [lastResult, setLastResult] = useState<VerificationResult | null>(null);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const result = await onVerify();
      setLastResult(result);
    } finally {
      setIsVerifying(false);
    }
  };

  const RecordStatus = ({
    valid,
    label,
    expected,
    found,
  }: {
    valid: boolean;
    label: string;
    expected: string;
    found: string[];
  }) => (
    <div className={`p-4 rounded-lg border ${valid ? 'border-primary/30 bg-primary/5' : 'border-destructive/30 bg-destructive/5'}`}>
      <div className="flex items-center gap-2 mb-3">
        {valid ? (
          <CheckCircle2 className="h-5 w-5 text-primary" />
        ) : (
          <XCircle className="h-5 w-5 text-destructive" />
        )}
        <span className="font-medium">{label} Record</span>
        <Badge variant={valid ? 'default' : 'destructive'} className="ml-auto">
          {valid ? 'Verified' : 'Not Found'}
        </Badge>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between p-2 bg-muted rounded font-mono text-xs">
          <div>
            <span className="text-muted-foreground">Expected: </span>
            {expected}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => copyToClipboard(expected, label)}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>

        {found.length > 0 && (
          <div className="p-2 bg-muted/50 rounded font-mono text-xs">
            <span className="text-muted-foreground">Found: </span>
            {found.join(', ') || 'None'}
          </div>
        )}

        {!valid && (
          <p className="text-muted-foreground text-xs mt-2">
            {label === 'A' 
              ? `Add an A record pointing to ${expected}`
              : `Add a TXT record at _lovable.${domain} with the verification token`
            }
          </p>
        )}
      </div>
    </div>
  );

  if (domainVerified) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 rounded-lg">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-primary" />
            <div>
              <p className="font-medium text-primary">{domain}</p>
              <p className="text-sm text-muted-foreground">Domain verified and active</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleVerify} disabled={isVerifying}>
              {isVerifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemoveDomain}>
              Remove
            </Button>
          </div>
        </div>

        {lastVerificationCheck && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Last checked: {formatDistanceToNow(new Date(lastVerificationCheck), { addSuffix: true })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Domain Header */}
      <div className="flex items-center justify-between p-4 bg-accent border border-border rounded-lg">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-warning" />
          <div>
            <p className="font-medium">{domain}</p>
            <p className="text-sm text-muted-foreground">Pending DNS verification</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleVerify} disabled={isVerifying}>
            {isVerifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Verify DNS
              </>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onRemoveDomain}>
            Remove
          </Button>
        </div>
      </div>

      {/* DNS Instructions */}
      <div className="p-4 border rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">DNS Configuration Required</h4>
          <a
            href="https://docs.lovable.dev/features/custom-domain"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            View Documentation <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        
        <p className="text-sm text-muted-foreground">
          Add these DNS records at your domain registrar:
        </p>

        <div className="grid gap-4">
          {/* A Record Instruction */}
          <RecordStatus
            valid={lastResult?.aRecordValid ?? false}
            label="A"
            expected="185.158.133.1"
            found={lastResult?.details.foundARecords ?? []}
          />

          {/* TXT Record Instruction */}
          <RecordStatus
            valid={lastResult?.txtRecordValid ?? false}
            label="TXT"
            expected={domainVerificationToken}
            found={lastResult?.details.foundTxtRecords ?? []}
          />
        </div>

        {/* Errors Display */}
        {(lastResult?.errors?.length ?? 0) > 0 && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-sm font-medium text-destructive mb-2">Issues Found:</p>
            <ul className="text-sm text-destructive/80 space-y-1">
              {lastResult?.errors.map((error, i) => (
                <li key={i} className="flex items-start gap-2">
                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>DNS changes can take up to 48 hours to propagate.</span>
        </div>
      </div>
    </div>
  );
}
